/**
 * The project segment of the URL, read straight from the pathname.
 *
 * Why this is its own pure module: the active project scope is authoritative in
 * the URL (`/projects/<slug>/...`), but the root layout resolves it ONCE (server
 * `resolveScopeFromPath` → `x-pathname`) and Next.js preserves that layout across
 * soft navigations, so the server `scope`/`projectSlug` props it threads into the
 * chrome go stale the moment you navigate without a full reload. The client
 * chrome (the top-nav project switcher + the ModeRail) therefore re-derives the
 * project from the LIVE `usePathname()` instead — and this is the one place the
 * "what counts as a project segment" rule lives, so the server resolver and both
 * client surfaces can never disagree.
 *
 * Pure + client-safe (no React, no I/O) — imported by the server scope resolver
 * AND the client chrome.
 */

/**
 * Reserved first segments under `/projects/` that are NOT project slugs — the
 * project-management surfaces (`/projects`, `/projects/map`, `/projects/new`).
 */
export const RESERVED_PROJECT_SEGMENTS = new Set(["sections", "map", "new"]);

/**
 * Extract the active project slug from a pathname, or `null` for the workspace
 * ("All projects") tier.
 *
 * Does NOT validate the slug against the project catalog: if the router is at
 * `/projects/<slug>/…`, that slug IS the current project (the page rendered, or
 * it would have `notFound()`-ed), so the path is authoritative. Callers that need
 * the project NAME (the switcher label) still look the slug up in the catalog;
 * callers that only build hrefs (the rail's `modePath`/`isModeActive`) use the
 * slug directly.
 */
export function projectSlugFromPath(pathname: string): string | null {
  const m = pathname.match(/^\/projects\/([^/?#]+)/);
  const seg = m?.[1] ? decodeURIComponent(m[1]) : null;
  if (seg && !RESERVED_PROJECT_SEGMENTS.has(seg)) return seg;
  return null;
}
